import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Input,
  Label,
  Spinner,
  Button,
  Collapse,
  Badge,
  Progress,
} from 'reactstrap';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { reportsAPI } from '../../helpers/api';
import { useAuth } from '../../helpers/hooks';
import { useIsMobile } from '../../helpers/useMediaQuery';
import SideModal from '../../components/Common/SideModal';

// Theme renkleri
const COLORS = {
  primary: '#405189',
  success: '#0ab39c',
  warning: '#f7b84b',
  danger: '#f06548',
  info: '#299cdb',
  secondary: '#3577f1',
};

interface DashboardStats {
  projects: { active: number; completed: number; suspended: number; cancelled: number };
  waybills: { scanned: number; matched: number; pending: number; notArrived: number; manual: number };
  dailyRecords: { date: string; count: number }[];
  vehicles: { companyVehicle: number; companyMachinery: number; rentalVehicle: number; rentalMachinery: number };
  fuel: {
    days: string[];
    byFuelType: { type: string; label: string; data: number[] }[];
    byVehicle: { plate: string; totalQuantity: number }[];
  };
  personnel: { active: number; inactive: number };
  personnelByProject: { projectName: string; count: number }[];
}

interface WorkProgressProject {
  projectId: string;
  projectName: string;
  siteCount: number;
  workCount: number;
  overallProgress: number | null;
  sites: Array<{
    siteId: string;
    siteName: string;
    works: Array<{
      workName: string;
      unit: string;
      target: number | null;
      completed: number;
      progress: number | null;
    }>;
  }>;
}

// Ayın ilk günü
const getFirstDayOfMonth = () => {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
};

// Bugün
const getToday = () => {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(getFirstDayOfMonth);
  const [endDate, setEndDate] = useState(getToday);
  const [showFilters, setShowFilters] = useState(false);
  const hasActiveFilters = startDate !== getFirstDayOfMonth() || endDate !== getToday();

  // İş ilerleme
  const [workProgress, setWorkProgress] = useState<WorkProgressProject[]>([]);
  const [workLoading, setWorkLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<WorkProgressProject | null>(null);
  const [projectDetailOpen, setProjectDetailOpen] = useState(false);

  const openProjectDetail = (project: WorkProgressProject) => {
    setSelectedProject(project);
    setProjectDetailOpen(true);
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 70) return 'success';
    if (progress >= 30) return 'warning';
    return 'danger';
  };

  const getProgressHex = (progress: number | null) => {
    if (progress === null) return COLORS.secondary;
    if (progress >= 70) return COLORS.success;
    if (progress >= 30) return COLORS.warning;
    return COLORS.danger;
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getDashboardStats(startDate, endDate);
      setStats(res.data);
    } catch (err) {
      console.error('Dashboard stats error:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const fetchWorkProgress = useCallback(async () => {
    setWorkLoading(true);
    try {
      const res = await reportsAPI.getWorkProgress();
      setWorkProgress(res.data);
    } catch (err) {
      console.error('Work progress error:', err);
    } finally {
      setWorkLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchWorkProgress();
  }, [fetchWorkProgress]);

  // --- Donut chart helper ---
  const donutOptions = (labels: string[], colors: string[]): ApexOptions => ({
    chart: { type: 'donut' },
    labels,
    colors,
    legend: { position: 'bottom' },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: { show: true, label: 'Toplam', fontSize: '14px' },
          },
        },
      },
    },
    dataLabels: { enabled: false },
    responsive: [{ breakpoint: 480, options: { chart: { height: 260 }, legend: { position: 'bottom' } } }],
  });

  // --- Proje Donut ---
  const projectSeries = stats
    ? [stats.projects.active, stats.projects.completed, stats.projects.suspended, stats.projects.cancelled]
    : [];
  const projectOptions = donutOptions(
    ['Aktif', 'Tamamlanan', 'Askıda', 'İptal'],
    [COLORS.success, COLORS.primary, COLORS.warning, COLORS.danger],
  );

  // --- İrsaliye Horizontal Bar (5 durum) ---
  const waybillData = stats
    ? [stats.waybills.scanned, stats.waybills.matched, stats.waybills.pending, stats.waybills.notArrived, stats.waybills.manual]
    : [];
  const waybillBarOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '55%', distributed: true } },
    colors: [COLORS.info, COLORS.success, COLORS.warning, COLORS.danger, COLORS.secondary],
    dataLabels: { enabled: true, style: { fontSize: '12px', fontWeight: 600 } },
    xaxis: { categories: ['Okutuldu', 'Eşleşti', 'Bekliyor', 'Gelmedi', 'Manuel'] },
    legend: { show: false },
    tooltip: { y: { formatter: (val: number) => `${val} adet` } },
  };
  const waybillBarSeries = [{ name: 'İrsaliye', data: waybillData }];

  // --- Araç RadialBar (4 kategori: sahiplik x makine) ---
  const vehicleTotal = stats
    ? stats.vehicles.companyVehicle + stats.vehicles.companyMachinery + stats.vehicles.rentalVehicle + stats.vehicles.rentalMachinery
    : 1;
  const vehicleRadialSeries = stats && vehicleTotal > 0
    ? [
        Math.round((stats.vehicles.companyVehicle / vehicleTotal) * 100),
        Math.round((stats.vehicles.companyMachinery / vehicleTotal) * 100),
        Math.round((stats.vehicles.rentalVehicle / vehicleTotal) * 100),
        Math.round((stats.vehicles.rentalMachinery / vehicleTotal) * 100),
      ]
    : [];
  const vehicleRadialOptions: ApexOptions = {
    chart: { type: 'radialBar' },
    plotOptions: {
      radialBar: {
        dataLabels: {
          name: { fontSize: '12px' },
          value: { fontSize: '14px', formatter: (val: number) => `%${val}` },
          total: {
            show: true,
            label: 'Toplam',
            formatter: () => `${vehicleTotal}`,
          },
        },
        track: { background: '#f3f3f9' },
      },
    },
    colors: [COLORS.primary, COLORS.warning, COLORS.info, COLORS.danger],
    labels: ['Şirket Araç', 'Şirket Makine', 'Kiralık Araç', 'Kiralık Makine'],
    legend: { show: true, position: 'bottom', fontSize: '12px' },
  };

  // --- Günlük Kayıtlar Area Chart ---
  const dailyRecordOptions: ApexOptions = {
    chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false } },
    colors: [COLORS.primary],
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 } },
    xaxis: {
      categories: stats?.dailyRecords.map((d) => {
        const dt = new Date(d.date);
        return `${dt.getDate()}/${dt.getMonth() + 1}`;
      }) || [],
      labels: { rotate: -45, style: { fontSize: '11px' } },
    },
    yaxis: { title: { text: 'Kayıt Sayısı' }, min: 0 },
    tooltip: { x: { format: 'dd/MM' } },
  };
  const dailyRecordSeries = [{ name: 'Günlük Kayıt', data: stats?.dailyRecords.map((d) => d.count) || [] }];

  // --- Yakıt Tüketimi Stacked Bar (Yakıt Türü Bazlı, Günlük) ---
  const fuelDays = stats?.fuel.days.map((d) => {
    const dt = new Date(d);
    return `${dt.getDate()}/${dt.getMonth() + 1}`;
  }) || [];

  const fuelTypeColors: Record<string, string> = { DIESEL: COLORS.warning, GASOLINE: COLORS.info, LPG: COLORS.success };
  const fuelOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false }, stacked: true },
    colors: stats?.fuel.byFuelType.map((f) => fuelTypeColors[f.type] || COLORS.secondary) || [],
    plotOptions: { bar: { columnWidth: '55%', borderRadius: 4, borderRadiusWhenStacked: 'last' as any } },
    dataLabels: { enabled: false },
    xaxis: { categories: fuelDays, labels: { rotate: -45, style: { fontSize: '11px' } } },
    yaxis: { title: { text: 'Litre' } },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val: number) => `${val} Lt` },
    },
    legend: { position: 'bottom' },
  };
  const fuelSeries = stats?.fuel.byFuelType.map((f) => ({
    name: f.label,
    data: f.data,
  })) || [];

  // --- Araç Yakıt Raporu Horizontal Bar ---
  const vehicleFuelData = stats?.fuel.byVehicle || [];
  const vehicleFuelOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true } },
    colors: vehicleFuelData.map((_, i) =>
      [COLORS.primary, COLORS.success, COLORS.warning, COLORS.danger, COLORS.info, COLORS.secondary][i % 6],
    ),
    dataLabels: { enabled: true, style: { fontSize: '12px', fontWeight: 600 }, formatter: (val: number) => `${val} Lt` },
    xaxis: { categories: vehicleFuelData.map((v) => v.plate) },
    yaxis: { title: { text: '' } },
    legend: { show: false },
    tooltip: { y: { formatter: (val: number) => `${val} Litre` } },
  };
  const vehicleFuelSeries = [{ name: 'Yakıt', data: vehicleFuelData.map((v) => v.totalQuantity) }];

  // --- Personel RadialBar ---
  const personnelTotal = stats ? stats.personnel.active + stats.personnel.inactive : 1;
  const personnelRadialSeries = stats && personnelTotal > 0
    ? [
        Math.round((stats.personnel.active / personnelTotal) * 100),
        Math.round((stats.personnel.inactive / personnelTotal) * 100),
      ]
    : [];
  const personnelRadialOptions: ApexOptions = {
    chart: { type: 'radialBar' },
    plotOptions: {
      radialBar: {
        dataLabels: {
          name: { fontSize: '13px' },
          value: { fontSize: '14px', formatter: (val: number) => `%${val}` },
          total: {
            show: true,
            label: 'Toplam',
            formatter: () => `${personnelTotal}`,
          },
        },
        track: { background: '#f3f3f9' },
      },
    },
    colors: [COLORS.success, COLORS.danger],
    labels: ['Aktif', 'Pasif'],
    legend: { show: true, position: 'bottom' },
  };

  // --- Proje Bazlı Personel Horizontal Bar ---
  const personnelByProjectData = stats?.personnelByProject || [];
  const personnelByProjectOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true } },
    colors: personnelByProjectData.map((_, i) =>
      [COLORS.primary, COLORS.success, COLORS.warning, COLORS.info, COLORS.danger, COLORS.secondary][i % 6],
    ),
    dataLabels: { enabled: true, style: { fontSize: '12px', fontWeight: 600 }, formatter: (val: number) => `${val} kişi` },
    xaxis: { categories: personnelByProjectData.map((p) => p.projectName) },
    legend: { show: false },
    tooltip: { y: { formatter: (val: number) => `${val} personel` } },
  };
  const personnelByProjectSeries = [{ name: 'Personel', data: personnelByProjectData.map((p) => p.count) }];

  return (
    <>
      {/* Başlık + Tarih Filtresi */}
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Ana Ekran</h4>
            {!isMobile && (
              <div className="d-flex align-items-center gap-2">
                <Button
                  size="sm"
                  color="soft-primary"
                  onClick={() => { setStartDate('2020-01-01'); setEndDate('2030-12-31'); }}
                >
                  Tüm Tarihler
                </Button>
                <Button
                  size="sm"
                  color="soft-secondary"
                  onClick={() => { setStartDate(getFirstDayOfMonth()); setEndDate(getToday()); }}
                >
                  Bu Ay
                </Button>
                <Label className="mb-0 text-muted small">Başlangıç:</Label>
                <Input
                  type="date"
                  bsSize="sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ width: 150 }}
                />
                <Label className="mb-0 text-muted small">Bitiş:</Label>
                <Input
                  type="date"
                  bsSize="sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ width: 150 }}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Mobile Filters */}
      {isMobile && (
        <Row className="mb-3">
          <Col xs={12}>
            <button
              className="btn btn-sm btn-light w-100 mb-3 d-flex align-items-center justify-content-between"
              onClick={() => setShowFilters(!showFilters)}
            >
              <span>
                <i className="ri-filter-3-line me-2"></i>
                Filtreler
                {hasActiveFilters && <Badge color="primary" className="ms-2">1</Badge>}
              </span>
              <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line`}></i>
            </button>
            <Collapse isOpen={showFilters}>
              <div className="bg-light p-3 rounded mb-3">
                <div className="d-flex gap-2 mb-2">
                  <Button
                    size="sm"
                    color="soft-primary"
                    className="flex-grow-1"
                    onClick={() => { setStartDate('2020-01-01'); setEndDate('2030-12-31'); }}
                  >
                    Tüm Tarihler
                  </Button>
                  <Button
                    size="sm"
                    color="soft-secondary"
                    className="flex-grow-1"
                    onClick={() => { setStartDate(getFirstDayOfMonth()); setEndDate(getToday()); }}
                  >
                    Bu Ay
                  </Button>
                </div>
                <Row>
                  <Col xs={6}>
                    <label className="small text-muted mb-1">Başlangıç</label>
                    <Input
                      type="date"
                      bsSize="sm"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col xs={6}>
                    <label className="small text-muted mb-1">Bitiş</label>
                    <Input
                      type="date"
                      bsSize="sm"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                </Row>
              </div>
            </Collapse>
          </Col>
        </Row>
      )}

      {/* Hoş geldin mesajı */}
      <Row className="mb-3">
        <Col xs={12}>
          <Card>
            <CardBody className="py-2">
              <h5 className="mb-0">
                Hoş Geldin, {user?.firstName} {user?.lastName}!
                <span className="text-muted fs-6 ms-2">
                  ({user?.role?.displayName})
                </span>
              </h5>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* İş İlerleme — Kompakt Proje Grid */}
      <Row className="mb-3">
        <Col xs={12}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="mb-0">
              <i className="ri-bar-chart-grouped-line me-2 text-primary"></i>
              İş İlerleme Durumu
            </h6>
            {workProgress.length > 0 && (
              <span className="text-muted small">{workProgress.length} aktif proje</span>
            )}
          </div>
        </Col>
      </Row>
      {workLoading ? (
        <Row className="mb-3">
          <Col xs={12}>
            <div className="text-center py-3">
              <Spinner size="sm" color="primary" />
            </div>
          </Col>
        </Row>
      ) : workProgress.length === 0 ? (
        <Row className="mb-3">
          <Col xs={12}>
            <Card>
              <CardBody>
                <p className="text-muted text-center mb-0">Aktif projede tanımlı iş bulunamadı.</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : (
        <Row className="mb-3">
          {workProgress.map((project) => (
            <Col key={project.projectId} xs={6} md={4} lg={3} className="mb-3">
              <Card
                className="card-animate mb-0 h-100"
                style={{ cursor: 'pointer' }}
                onClick={() => openProjectDetail(project)}
              >
                <CardBody className="p-3 d-flex flex-column align-items-center text-center">
                  <div className="position-relative mb-2" style={{ width: 80, height: 80 }}>
                    <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                      <circle
                        cx="18" cy="18" r="15.915"
                        fill="none"
                        stroke="#f3f3f9"
                        strokeWidth="3"
                      />
                      <circle
                        cx="18" cy="18" r="15.915"
                        fill="none"
                        stroke={getProgressHex(project.overallProgress)}
                        strokeWidth="3"
                        strokeDasharray={`${project.overallProgress ?? 0} ${100 - (project.overallProgress ?? 0)}`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div
                      className="position-absolute top-50 start-50 translate-middle"
                      style={{ fontWeight: 700, fontSize: '0.85rem', color: getProgressHex(project.overallProgress) }}
                    >
                      {project.overallProgress !== null ? `%${project.overallProgress}` : '-'}
                    </div>
                  </div>
                  <h6 className="mb-1" style={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.3 }}>
                    {project.projectName}
                  </h6>
                  <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {project.siteCount} şantiye • {project.workCount} iş
                  </span>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      ) : stats ? (
        <>
          {/* Sıra 1: Proje Durumları + Proje Bazlı Personel */}
          <Row>
            <Col lg={6}>
              <Card>
                <CardHeader className="py-2">
                  <h6 className="card-title mb-0">Proje Durumları</h6>
                </CardHeader>
                <CardBody>
                  {projectSeries.some((v) => v > 0) ? (
                    <Chart options={projectOptions} series={projectSeries} type="donut" height={300} />
                  ) : (
                    <p className="text-muted text-center py-4">Veri bulunamadı</p>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <CardHeader className="py-2">
                  <h6 className="card-title mb-0">Proje Bazlı Personel</h6>
                </CardHeader>
                <CardBody>
                  {personnelByProjectData.length > 0 ? (
                    <Chart options={personnelByProjectOptions} series={personnelByProjectSeries} type="bar" height={300} />
                  ) : (
                    <p className="text-muted text-center py-4">Veri bulunamadı</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Sıra 2: İrsaliye Durumları + Günlük Kayıtlar */}
          <Row>
            <Col lg={6}>
              <Card>
                <CardHeader className="py-2">
                  <h6 className="card-title mb-0">İrsaliye Durumları</h6>
                </CardHeader>
                <CardBody>
                  {waybillData.some((v) => v > 0) ? (
                    <Chart options={waybillBarOptions} series={waybillBarSeries} type="bar" height={300} />
                  ) : (
                    <p className="text-muted text-center py-4">Veri bulunamadı</p>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <CardHeader className="py-2">
                  <h6 className="card-title mb-0">Günlük Kayıtlar</h6>
                </CardHeader>
                <CardBody>
                  {dailyRecordSeries[0].data.length > 0 ? (
                    <Chart options={dailyRecordOptions} series={dailyRecordSeries} type="area" height={300} />
                  ) : (
                    <p className="text-muted text-center py-4">Veri bulunamadı</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Sıra 3: Araç Yakıt Raporu + Yakıt Tüketimi */}
          <Row>
            <Col lg={6}>
              <Card>
                <CardHeader className="py-2">
                  <h6 className="card-title mb-0">Araç Yakıt Raporu</h6>
                </CardHeader>
                <CardBody>
                  {vehicleFuelData.length > 0 ? (
                    <Chart options={vehicleFuelOptions} series={vehicleFuelSeries} type="bar" height={300} />
                  ) : (
                    <p className="text-muted text-center py-4">Veri bulunamadı</p>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <CardHeader className="py-2">
                  <h6 className="card-title mb-0">Yakıt Tüketimi</h6>
                </CardHeader>
                <CardBody>
                  {fuelSeries.length > 0 ? (
                    <Chart options={fuelOptions} series={fuelSeries} type="bar" height={300} />
                  ) : (
                    <p className="text-muted text-center py-4">Veri bulunamadı</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Sıra 4: Araç Dağılımı + Personel Durumu */}
          <Row>
            <Col lg={6}>
              <Card>
                <CardHeader className="py-2">
                  <h6 className="card-title mb-0">Araç Dağılımı</h6>
                </CardHeader>
                <CardBody>
                  {vehicleRadialSeries.some((v) => v > 0) ? (
                    <Chart options={vehicleRadialOptions} series={vehicleRadialSeries} type="radialBar" height={300} />
                  ) : (
                    <p className="text-muted text-center py-4">Veri bulunamadı</p>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <CardHeader className="py-2">
                  <h6 className="card-title mb-0">Personel Durumu</h6>
                </CardHeader>
                <CardBody>
                  {personnelRadialSeries.some((v) => v > 0) ? (
                    <Chart options={personnelRadialOptions} series={personnelRadialSeries} type="radialBar" height={300} />
                  ) : (
                    <p className="text-muted text-center py-4">Veri bulunamadı</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

        </>
      ) : (
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <p className="text-muted text-center mb-0">İstatistikler yüklenemedi.</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Proje Detay SideModal */}
      <SideModal
        isOpen={projectDetailOpen}
        toggle={() => { setProjectDetailOpen(false); setSelectedProject(null); }}
        title={selectedProject ? selectedProject.projectName : 'Proje Detayı'}
        size="lg"
      >
        {selectedProject && (
          <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
            {/* Özet Kart */}
            <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-1" style={{ fontWeight: 600 }}>{selectedProject.projectName}</h5>
                  <span className="text-muted small">
                    {selectedProject.siteCount} şantiye • {selectedProject.workCount} iş
                  </span>
                </div>
                <div className="text-center">
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: getProgressHex(selectedProject.overallProgress) }}>
                    {selectedProject.overallProgress !== null ? `%${selectedProject.overallProgress}` : '-'}
                  </div>
                  <span className="text-muted" style={{ fontSize: '0.7rem' }}>Genel İlerleme</span>
                </div>
              </div>
            </div>

            {/* Şantiye Detayları */}
            {selectedProject.sites.map((site) => (
              <div
                key={site.siteId}
                className="rounded-3 p-3 mb-3"
                style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }}
              >
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="ri-building-line text-warning"></i>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{site.siteName}</span>
                  <span className="text-muted small ms-auto">{site.works.length} iş</span>
                </div>
                <div className="d-flex flex-column gap-3">
                  {site.works.map((work, idx) => (
                    <div key={idx}>
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{work.workName}</span>
                        <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {work.target !== null ? (
                            <>
                              {Number(work.completed).toLocaleString('tr-TR')} / {Number(work.target).toLocaleString('tr-TR')} {work.unit}
                            </>
                          ) : (
                            <>
                              {Number(work.completed).toLocaleString('tr-TR')} {work.unit}
                            </>
                          )}
                        </span>
                      </div>
                      {work.progress !== null ? (
                        <div className="d-flex align-items-center gap-2">
                          <Progress
                            value={work.progress}
                            color={getProgressColor(work.progress)}
                            style={{ height: '8px', flex: 1 }}
                          />
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, minWidth: 35, color: getProgressHex(work.progress) }}>
                            %{work.progress}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>Hedef belirlenmemiş</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </SideModal>
    </>
  );
};

export default Dashboard;
